import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Helper function to safely convert any value to string
const safeStringValue = (value: any): string => {
  if (value === null || value === undefined) {
    return ""
  }
  if (typeof value === "string") {
    return value
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return String(value)
}

// Helper function to get status color classes based on stage/status
export const getStageStatusColor = (stage: string | any): string => {
  const stageStr = safeStringValue(stage)
  if (!stageStr) {
    return "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-700 border-slate-200/50 shadow-sm"
  }

  const stageUpper = stageStr.toUpperCase()
  switch (stageUpper) {
    case "COMPLETED":
    case "COMPLETE":
    case "DELIVERED":
    case "FINISHED":
      return "bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border-emerald-200/50 shadow-sm"
    case "IN_PROGRESS":
    case "ACTIVE":
    case "WORKING":
    case "PROCESSING":
      return "bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border-blue-200/50 shadow-sm"
    case "PENDING":
    case "WAITING":
    case "QUEUED":
      return "bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border-amber-200/50 shadow-sm"
    case "CANCELLED":
    case "REJECTED":
    case "FAILED":
      return "bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border-red-200/50 shadow-sm"
    case "ON_HOLD":
    case "PAUSED":
      return "bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 border-orange-200/50 shadow-sm"
    case "ADDED":
      return "bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border-blue-200/50 shadow-sm"
    case "ADD_OPPS":
      return "bg-gradient-to-r from-purple-50 to-violet-50 text-purple-700 border-purple-200/50 shadow-sm"
    case "CLIENT_REVIEW":
      return "bg-gradient-to-r from-yellow-50 to-amber-50 text-yellow-700 border-yellow-200/50 shadow-sm"
    case "FULFILLMENT":
      return "bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 border-indigo-200/50 shadow-sm"
    case "CREATED":
      return "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-700 border-slate-200/50 shadow-sm"
    default:
      return "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-700 border-slate-200/50 shadow-sm"
  }
}

// Helper function to format stage display name
export const formatStageDisplayName = (stage: string | any): string => {
  const stageStr = safeStringValue(stage)
  if (!stageStr) return "Unknown"
  return stageStr
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase())
}

// Helper function to truncate breadcrumb text (applies to all items except the last one)
export function truncateBreadcrumbText(text: string | null | undefined, maxLength: number = 20): string {
  if (!text) return ""
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + "..."
}
